import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import type { FeedItem } from "../types/Post/post";

interface UseProfilePostsResult {
  items: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
  updateItem: (postId: string, updater: (item: FeedItem) => FeedItem) => void;
  removeItem: (postId: string) => void;
  restoreItem: (item: FeedItem, index: number) => void;
}

/**
 * Hook for managing profile posts with cursor-based pagination
 *
 * Features:
 * - Fetches posts for a given userId on mount and when userId changes
 * - Supports infinite scroll via loadMore
 * - Deduplicates items by post._id when appending pages
 * - Provides optimistic mutation support via updateItem / removeItem
 * - Provides refresh functionality to reset and reload
 *
 * @param userId - The profile owner's userId, or null to skip fetching
 * @returns Profile posts state and control functions
 */
export function useProfilePosts(userId: string | null): UseProfilePostsResult {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  // Track the current userId to avoid stale responses updating state
  const currentUserIdRef = useRef<string | null>(userId);

  /**
   * Fetches profile posts from the API
   * @param targetUserId - The user whose posts to fetch
   * @param cursorValue - Optional cursor for pagination
   * @param append - Whether to append results or replace
   */
  const fetchPosts = useCallback(
    async (targetUserId: string, cursorValue: string | null, append: boolean) => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.social.getUserPosts(targetUserId, cursorValue || undefined);

        // Guard against stale responses if userId changed during the request
        if (currentUserIdRef.current !== targetUserId) {
          return;
        }

        if (append) {
          // Append new items, deduplicating by post._id
          setItems((prevItems) => {
            const existingIds = new Set(prevItems.map((item) => item.post._id));
            const newItems = response.items.filter((item) => !existingIds.has(item.post._id));
            return [...prevItems, ...newItems];
          });
        } else {
          // Replace items (initial load or refresh)
          setItems(response.items);
        }

        setCursor(response.cursor);
        setHasMore(response.hasMore);
      } catch (err) {
        // Guard against stale responses
        if (currentUserIdRef.current !== targetUserId) {
          return;
        }
        const errorMessage = err instanceof Error ? err.message : "Failed to load posts";
        setError(errorMessage);
      } finally {
        // Guard against stale responses
        if (currentUserIdRef.current === targetUserId) {
          setLoading(false);
        }
      }
    },
    []
  );

  /**
   * Load more items using the current cursor
   * Guarded by hasMore and loading state
   */
  const loadMore = useCallback(() => {
    if (!hasMore || loading || !userId) {
      return;
    }
    fetchPosts(userId, cursor, true);
  }, [hasMore, loading, userId, cursor, fetchPosts]);

  /**
   * Refresh the posts from the beginning
   * Clears existing items and resets cursor
   */
  const refresh = useCallback(() => {
    if (!userId) return;
    setCursor(null);
    setItems([]);
    fetchPosts(userId, null, false);
  }, [userId, fetchPosts]);

  /**
   * Update a single item by postId (for optimistic like/edit updates)
   * @param postId - The post to update
   * @param updater - Function that returns the updated item
   */
  const updateItem = useCallback((postId: string, updater: (item: FeedItem) => FeedItem) => {
    setItems((prev) => prev.map((item) => (item.post._id === postId ? updater(item) : item)));
  }, []);

  /**
   * Remove an item by postId (for optimistic delete)
   * @param postId - The post to remove
   */
  const removeItem = useCallback((postId: string) => {
    setItems((prev) => prev.filter((item) => item.post._id !== postId));
  }, []);

  /**
   * Restore a previously removed item at its original index (for rollback on delete failure)
   * @param item - The item to restore
   * @param index - The index at which to insert the item
   */
  const restoreItem = useCallback((item: FeedItem, index: number) => {
    setItems((prev) => {
      const newItems = [...prev];
      newItems.splice(index, 0, item);
      return newItems;
    });
  }, []);

  // Fetch posts on mount and when userId changes
  useEffect(() => {
    currentUserIdRef.current = userId;

    if (!userId) {
      setItems([]);
      setCursor(null);
      setHasMore(false);
      setError(null);
      setLoading(false);
      return;
    }

    // Reset state and fetch for the new userId
    setItems([]);
    setCursor(null);
    setHasMore(false);
    setError(null);
    fetchPosts(userId, null, false);
  }, [userId, fetchPosts]);

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    updateItem,
    removeItem,
    restoreItem,
  };
}
